import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { Link, useParams } from 'react-router-dom';
import apiService from '../../utils/api';

export interface ServicesSectionProps {
  description: string;
  salonId?: string;
}

interface Service {
  _id: string;
  name: string;
  description?: string;
  price: number;
  duration: number;
  category?: string;
  isActive?: boolean;
}

const ServicesSection: React.FC<ServicesSectionProps> = ({ description, salonId }) => {
  const [services, setServices] = useState<Service[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const { slug } = useParams<{ slug: string }>();
  
  useEffect(() => {
    const fetchServices = async () => {
      if (!salonId) {
        setError('Salon kimliği bulunamadı');
        setLoading(false);
        return;
      }
      
      try {
        setLoading(true);
        const response = await apiService.getSalonServices(salonId);
        
        if (response.success && response.services) {
          console.log('Salon hizmetleri yüklendi:', response.services);
          setServices(response.services);
        } else {
          setError('Hizmetler alınamadı');
        }
      } catch (err) {
        console.error('Hizmetler yüklenirken hata:', err);
        setError('Hizmetler yüklenirken bir hata oluştu');
      } finally {
        setLoading(false);
      }
    };
    
    fetchServices();
  }, [salonId]);
  
  if (loading) {
    return (
      <ServicesSectionContainer>
        <div className="container">
          <SectionTitleContainer>
            <SectionTitle>Hizmetlerimiz</SectionTitle>
            <SectionSubtitle>{description}</SectionSubtitle>
          </SectionTitleContainer>
          <LoadingMessage>Hizmetler yükleniyor...</LoadingMessage>
        </div>
      </ServicesSectionContainer>
    );
  }
  
  if (error || services.length === 0) {
    return (
      <ServicesSectionContainer>
        <div className="container">
          <SectionTitleContainer>
            <SectionTitle>Hizmetlerimiz</SectionTitle>
            <SectionSubtitle>{description}</SectionSubtitle>
          </SectionTitleContainer>
          <ErrorMessage>
            {error || 'Henüz hizmet bulunmamaktadır.'}
          </ErrorMessage>
        </div>
      </ServicesSectionContainer>
    );
  }
  
  return (
    <ServicesSectionContainer id="services">
      <div className="container">
        <SectionTitleContainer>
          <SectionTitle>Hizmetlerimiz</SectionTitle>
          <SectionSubtitle>{description}</SectionSubtitle>
        </SectionTitleContainer>
        
        <ServicesTableContainer>
          <ServiceRow header>
            <HeaderCell>Hizmet</HeaderCell>
            <HeaderCell>Süre</HeaderCell>
            <HeaderCell>Fiyat</HeaderCell>
            <HeaderCell></HeaderCell>
          </ServiceRow>
          {services.slice(0, 10).map((service: Service, index: number) => (
            <ServiceRow key={service._id} even={index % 2 === 1}>
              <ServiceInfo>
                <ServiceName>{service.name}</ServiceName>
              </ServiceInfo>
              <ServiceDuration>
                <span className="material-icons">schedule</span>
                {service.duration} dk
              </ServiceDuration>
              <ServicePrice>{service.price} ₺</ServicePrice>
              <ButtonCell>
                <AppointmentButton to={`/${slug}/randevu?service=${service._id}`}>
                  Randevu Al
                </AppointmentButton>
              </ButtonCell>
            </ServiceRow>
          ))}
        </ServicesTableContainer>
        
        <ViewAllButtonContainer>
          <ViewAllButton to={`/${slug}/hizmetler`}>
            Tüm Hizmetleri Görüntüle
            <span className="material-icons">arrow_forward</span>
          </ViewAllButton>
        </ViewAllButtonContainer>
      </div>
    </ServicesSectionContainer>
  );
};

const ServicesSectionContainer = styled.section`
  padding: 80px 0;
  background-color: ${({ theme }) => theme.colors.background};
`;

const SectionTitleContainer = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  margin-bottom: 40px;
  text-align: center;
`;

const SectionTitle = styled.h2`
  text-align: center;
  margin-bottom: 15px;
  position: relative;
  background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  display: inline-block;
  font-size: 2.5rem;
  
  &:after {
    content: '';
    display: block;
    width: 60px;
    height: 3px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    margin: 15px auto 0;
  }
`;

const SectionSubtitle = styled.p`
  text-align: center;
  color: ${({ theme }) => theme.colors.lightText};
  max-width: 700px;
  margin: 0 auto;
  font-size: 1.1rem;
  line-height: 1.6;
`;

const ServicesTableContainer = styled.div`
  width: 100%;
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  overflow: hidden;
  box-shadow: ${({ theme }) => theme.shadows.small};
`;

const LoadingMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: ${({ theme }) => theme.colors.lightText};
  font-style: italic;
`;

const ErrorMessage = styled.div`
  text-align: center;
  padding: 30px;
  color: ${({ theme }) => theme.colors.error};
  font-style: italic;
`;

interface RowProps {
  even?: boolean;
  header?: boolean;
}

const ServiceRow = styled.div<RowProps>`
  display: grid;
  grid-template-columns: 2fr 1fr 1fr 1fr;
  align-items: center;
  padding: 16px 24px;
  background: ${({ even, header, theme }) => 
    header 
      ? `linear-gradient(to right, ${theme.colors.primary}, ${theme.colors.secondary})` 
      : even ? '#F5F5F5' : 'white'};
  color: ${({ header }) => header ? 'white' : 'inherit'};
  
  &:not(:last-child) {
    border-bottom: 1px solid ${({ theme }) => theme.colors.border};
  }
`;

const ServiceInfo = styled.div`
  display: flex;
  flex-direction: column;
`;

const ServiceName = styled.span`
  font-weight: 600;
  color: ${({ theme }) => theme.colors.text};
`;

const ServiceDuration = styled.div`
  display: flex;
  align-items: center;
  color: ${({ theme }) => theme.colors.lightText};
  
  span {
    font-size: 16px;
    margin-right: 5px;
  }
`;

const ServicePrice = styled.span`
  color: ${({ theme }) => theme.colors.primary};
  font-weight: 600;
  font-size: 1.1rem;
`;

const ButtonCell = styled.div`
  text-align: right;
`;

const AppointmentButton = styled(Link)`
  display: inline-block;
  text-align: center;
  background-color: transparent;
  color: ${({ theme }) => theme.colors.primary};
  padding: 10px 22px;
  position: relative;
  border-radius: 50px;
  text-decoration: none;
  font-weight: 500;
  white-space: nowrap;
  transition: all 0.3s;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    inset: 0;
    border-radius: 50px;
    padding: 2px;
    background: linear-gradient(to right, ${({ theme }) => theme.colors.primary}, ${({ theme }) => theme.colors.secondary});
    mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
    -webkit-mask-composite: xor;
    mask-composite: exclude;
    z-index: -1;
  }
  
  &:hover {
    transform: translateY(-3px);
    box-shadow: ${({ theme }) => theme.shadows.medium};
    color: ${({ theme }) => theme.colors.primary};
  }
`;

const ViewAllButtonContainer = styled.div`
  display: flex;
  justify-content: center;
  margin-top: 50px;
`;

const ViewAllButton = styled(Link)`
  display: flex;
  align-items: center;
  gap: 10px;
  color: ${({ theme }) => theme.colors.primary};
  text-decoration: none;
  font-weight: 600;
  transition: transform 0.3s ease;
  padding: 10px 20px;
  border: 2px solid ${({ theme }) => theme.colors.primary};
  border-radius: ${({ theme }) => theme.borderRadius.medium};
  
  span {
    transition: transform 0.3s ease;
  }
  
  &:hover {
    transform: translateY(-3px);
    
    span {
      transform: translateX(5px);
    }
  }
`;

const HeaderCell = styled.div`
  font-weight: 600;
  color: white;
  
  &:last-child {
    text-align: right;
  }
`;

export default ServicesSection; 